import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Search, Sparkles, MapPin, UserPlus, CheckCircle2, Send } from "lucide-react";
import api from "../lib/apiClient";
import { toast } from "sonner";

// Découverte d'artistes (browse + filtres) et Matching IA (matchMode). Réutilise le moteur existant.
export default function ManagerDiscover({ matchMode = false }) {
  const [items, setItems] = useState(null);
  const [search, setSearch] = useState("");
  const [location, setLocation] = useState("");
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const loadBrowse = useCallback(() => {
    setItems(null);
    const p = new URLSearchParams();
    if (search) p.set("search", search);
    if (location) p.set("location", location);
    api.get(`/manager/discover?${p}`).then((r) => setItems(r.data.artists)).catch(() => setItems([]));
  }, [search, location]);

  useEffect(() => { if (!matchMode) loadBrowse(); }, [matchMode, loadBrowse]);

  const runMatch = async () => {
    if (!query.trim()) { toast.error("Décris l'artiste recherché"); return; }
    setBusy(true); setItems(null);
    try {
      const { data } = await api.post("/manager/discover/match", { query, location: location || null });
      setItems(data.results);
    } catch (e) { toast.error(e.response?.data?.detail || "Échec du matching"); setItems([]); }
    finally { setBusy(false); }
  };

  const invite = async (a) => {
    try {
      await api.post(`/manager/discover/${a.id}/invite`);
      toast.success("Invitation envoyée");
      setItems((list) => list.map((x) => (x.id === a.id ? { ...x, state: "INVITED" } : x)));
    } catch (e) { toast.error(e.response?.data?.detail || "Échec de l'invitation"); }
  };

  return (
    <div className="space-y-6" data-testid={matchMode ? "manager-matching-page" : "manager-discover-page"}>
      <div>
        <p className="text-sm font-mono uppercase tracking-wider text-amber-500">Recrutement</p>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold mt-1">
          {matchMode ? "Matching IA" : "Découvrir des artistes"}
        </h1>
        <p className="text-zinc-500 mt-2 text-sm">
          {matchMode
            ? "Décris ton besoin : l'IA classe les artistes ouverts au management selon tes critères."
            : "Parcours les artistes qui ont choisi d'être visibles pour le management."}
        </p>
      </div>

      {matchMode ? (
        <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-5 space-y-3">
          <textarea value={query} onChange={(e) => setQuery(e.target.value)} rows={3} data-testid="match-query"
            placeholder="Ex : artiste pop/R&B ouvert au management, avec un projet actif, cherchant à développer sa visibilité."
            className="w-full px-4 py-3 rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-100 text-sm resize-none outline-none focus:border-amber-500" />
          <div className="flex gap-2">
            <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Localisation (optionnel)" data-testid="match-location"
              className="flex-1 h-11 px-3 bg-zinc-900 border border-zinc-800 rounded-xl text-sm text-zinc-100 outline-none focus:border-amber-500" />
            <button onClick={runMatch} disabled={busy} data-testid="match-run" className="h-11 px-5 rounded-xl gold-gradient text-black font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}Lancer le matching
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadBrowse()} placeholder="Rechercher par nom d'artiste" data-testid="discover-search"
              className="w-full h-11 pl-9 pr-3 bg-zinc-900 border border-zinc-800 rounded-xl text-sm text-zinc-100 outline-none focus:border-amber-500" />
          </div>
          <input value={location} onChange={(e) => setLocation(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadBrowse()} placeholder="Localisation" data-testid="discover-location"
            className="sm:w-52 h-11 px-3 bg-zinc-900 border border-zinc-800 rounded-xl text-sm text-zinc-100 outline-none focus:border-amber-500" />
          <button onClick={loadBrowse} data-testid="discover-search-btn" className="h-11 px-5 rounded-xl border border-zinc-700 text-zinc-200 text-sm hover:border-amber-500/50">Rechercher</button>
        </div>
      )}

      {items === null ? (
        <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 text-amber-500 animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-10 text-center" data-testid="discover-empty">
          <Search className="w-8 h-8 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-300 font-medium">Aucun artiste trouvé</p>
          <p className="text-sm text-zinc-500 mt-1">Ajuste tes critères ou reviens plus tard : de nouveaux artistes s'ouvrent au management régulièrement.</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((a) => <ArtistCard key={a.id} a={a} onInvite={invite} onOpen={() => nav(`/manager/discover/${a.id}`)} />)}
        </div>
      )}
    </div>
  );
}

function ArtistCard({ a, onInvite, onOpen }) {
  return (
    <div className="bg-[#18181E] border border-zinc-800 rounded-2xl p-5 flex flex-col" data-testid={`discover-artist-${a.id}`}>
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-full bg-zinc-800 overflow-hidden shrink-0">
          {a.avatar_url && <img src={a.avatar_url} alt="" className="w-full h-full object-cover" />}
        </div>
        <div className="min-w-0">
          <button onClick={onOpen} className="font-display font-bold text-zinc-100 truncate hover:text-amber-400">{a.artist_name}</button>
          <p className="text-xs text-zinc-500">{a.domain || "Artiste"} · Niveau {a.level}</p>
        </div>
        {a.match_score != null && (
          <span className="ml-auto text-[11px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/30 shrink-0" data-testid={`match-score-${a.id}`}>
            Correspondance IA {a.match_score}%
          </span>
        )}
      </div>
      {a.bio && <p className="text-sm text-zinc-400 mt-3 line-clamp-2">{a.bio}</p>}
      {a.match_reason && <p className="text-xs text-amber-300/80 mt-2 italic line-clamp-2">« {a.match_reason} »</p>}
      <div className="flex flex-wrap gap-1.5 mt-3">
        {(a.genres || []).slice(0, 3).map((g) => <span key={g} className="text-[11px] px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-300">{g}</span>)}
        {a.city && <span className="text-[11px] px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-400 inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{a.city}</span>}
      </div>
      <div className="mt-4 pt-3 border-t border-zinc-800/70">
        {a.state === "MANAGED" ? (
          <span className="text-sm text-emerald-400 inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" />Déjà dans ton équipe</span>
        ) : a.state === "INVITED" ? (
          <span className="text-sm text-amber-400 inline-flex items-center gap-1.5" data-testid={`invited-${a.id}`}><Send className="w-4 h-4" />Invitation envoyée</span>
        ) : (
          <button onClick={() => onInvite(a)} data-testid={`invite-${a.id}`} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl gold-gradient text-black font-semibold text-sm"><UserPlus className="w-4 h-4" />Inviter dans mon équipe</button>
        )}
      </div>
    </div>
  );
}
